import { Button, Typography } from '@breetometer/components';
import { DatePicker, ModalParts, NotificationBanner } from 'BreetComponents';
import { onFormPreventDefault } from 'BreetHelpers';
import { useFormData, useModalApi } from 'BreetHooks';
import { setExportEndDate, setExportStartDate, useAppDispatch } from 'BreetRedux';
import { format, parseISO } from 'date-fns';
import { useCallback } from 'react';

import { FileExportSchema, getEndDateBounds, getExportBlockingReason, getStartDateBounds } from './DialogFileExport.helpers';

const toDate = (iso?: string): Date | undefined => (iso ? parseISO(iso) : undefined);
const toIso = (date: Date | null): string => (date ? format(date, 'yyyy-MM-dd') : '');

const DialogFileExport = () => {
	const dispatch = useAppDispatch();

	const { closeModalDirect, handleModalClose } = useModalApi();

	const { isFormCompleted, hookFormProps } = useFormData(FileExportSchema, undefined, { mode: 'onChange' });

	const selectedStartDate = hookFormProps.watch('exportStartDate');
	const selectedEndDate = hookFormProps.watch('exportEndDate');

	const startDateBounds = getStartDateBounds(selectedEndDate);
	const endDateBounds = getEndDateBounds(selectedStartDate);

	const blockingReason = getExportBlockingReason([
		hookFormProps.formState.errors.exportStartDate?.type,
		hookFormProps.formState.errors.exportEndDate?.type,
	]);

	const onExport = useCallback(() => {
		dispatch(setExportStartDate(hookFormProps.watch('exportStartDate')));
		dispatch(setExportEndDate(hookFormProps.watch('exportEndDate')));
		closeModalDirect('fileExport');
	}, [closeModalDirect, dispatch, hookFormProps]);

	// setValue + re-validate the paired field, replacing the old register({ deps }) so the 6-month range
	// and after-start rules stay in sync when either date changes.
	const handleStartDateChange = useCallback(
		(date: Date | null) => {
			hookFormProps.setValue('exportStartDate', toIso(date), { shouldValidate: true, shouldDirty: true });
			hookFormProps.trigger('exportEndDate').catch(console.error);
		},
		[hookFormProps]
	);

	const handleEndDateChange = useCallback(
		(date: Date | null) => {
			hookFormProps.setValue('exportEndDate', toIso(date), { shouldValidate: true, shouldDirty: true });
			hookFormProps.trigger('exportStartDate').catch(console.error);
		},
		[hookFormProps]
	);

	return (
		<ModalParts.Wrapper
			title='Export'
			root='Export'
			handleBackLink={handleModalClose('fileExport')}
			rightSheet
		>
			<div className='dialog-export'>
				<Typography
					className='color-neutral-50'
					as='h5'
				>
					Select date range for your file export
				</Typography>
				<form
					className='new-drop-down-input'
					onSubmit={onFormPreventDefault}
				>
					<DatePicker
						label='Start Date'
						id='export-start-date'
						value={toDate(selectedStartDate) ?? null}
						onChange={handleStartDateChange}
						minDate={toDate(startDateBounds.min)}
						maxDate={toDate(startDateBounds.max)}
						portalId='datepicker-portal'
						required
					/>

					<DatePicker
						label='End Date'
						id='export-end-date'
						value={toDate(selectedEndDate) ?? null}
						onChange={handleEndDateChange}
						minDate={toDate(endDateBounds.min)}
						maxDate={toDate(endDateBounds.max)}
						portalId='datepicker-portal'
						required
					/>

					{!!blockingReason && (
						<p
							className='bt_input-error-message'
							role='alert'
						>
							{blockingReason}
						</p>
					)}

					<NotificationBanner
						type='standard'
						status='success'
						mood='black-white'
						message='Your file will be downloaded once the export is ready'
					/>

					<div className='cta'>
						<Button
							disabled={!isFormCompleted}
							type='button'
							onClick={onExport}
							className='primary-hover'
						>
							Export
						</Button>
					</div>
				</form>
			</div>
		</ModalParts.Wrapper>
	);
};

export default DialogFileExport;
